/**
 * The temporary chats this page knows of: made from Home with the ghost on,
 * or opened and found to be one.
 *
 * The sidebar never lists one — `upsertChat` and `touchChat` in state.tsx ask
 * `isTemporary` first, which covers every place that adds to it. Leaving one
 * deletes it: App.tsx watches the path and calls `discardTemporary` for the
 * chat left. One still open when the tab closes is left to the Worker's
 * hourly sweep.
 *
 * Module memory, as in chatStash.ts: a reload forgets, and the chat page marks
 * the chat again when it loads it.
 */

import { deleteChat } from '../api';
import { matchRoute } from '../router';
import { stopTurn } from './useTurns';

const temporary = new Set<string>();

export function markTemporary(id: string): void {
  temporary.add(id);
}

/** Saved: listed from now on, and kept when left. */
export function markSaved(id: string): void {
  temporary.delete(id);
}

export const isTemporary = (id: string): boolean => temporary.has(id);

/**
 * Put a temporary chat away: its reply stopped, then the chat deleted. A
 * delete that fails is left to the sweep. Anything not temporary, or saved
 * since, is left alone.
 */
export async function discardTemporary(id: string): Promise<void> {
  if (!temporary.has(id)) return;
  stopTurn(id);
  await deleteChat(id).catch(() => undefined);
  temporary.delete(id);
}

/** The chat left by moving from `prev` to `next`, or null when no chat was. */
export function leftChat(prev: string, next: string): string | null {
  const from = matchRoute('/chat/:id', prev)?.id;
  if (!from) return null;
  return matchRoute('/chat/:id', next)?.id === from ? null : from;
}
